import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

/**
 * apps/web :: components/notifications :: NotificationPanel
 *
 * Dropdown notification tray triggered by the bell button in
 * LearnerPortalShell. Currently renders an empty state — a backend
 * notification feed endpoint will populate it in a follow-up ticket.
 *
 * Posture: closes on Escape key, outside click, or explicit X button.
 * Focus management: opening does NOT steal focus from the bell button
 * so keyboard users stay on the trigger.
 */
export interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationPanel({
  isOpen,
  onClose,
}: NotificationPanelProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Slight delay so the bell-button click that opened the panel doesn't
    // immediately close it via the document listener.
    const id = setTimeout(() => document.addEventListener("mousedown", handler), 50);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handler);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={t("learner.portal.notifPanel.heading")}
      className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-lg z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">
          {t("learner.portal.notifPanel.heading")}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("learner.portal.notifPanel.close")}
          className="rounded p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
        >
          <X aria-hidden="true" size={14} />
        </button>
      </div>

      {/* Body — empty state */}
      <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
        <div
          aria-hidden="true"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-600 text-xl"
        >
          ✓
        </div>
        <p className="text-sm font-semibold text-slate-800">
          {t("learner.portal.notifPanel.emptyTitle")}
        </p>
        <p className="text-xs text-slate-500 leading-relaxed">
          {t("learner.portal.notifPanel.emptyBody")}
        </p>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-100 px-4 py-3">
        <Link
          to="/settings"
          onClick={onClose}
          className="text-xs font-medium text-teal-700 hover:text-teal-800 no-underline hover:underline"
        >
          {t("learner.portal.notifPanel.settingsLink")}
        </Link>
      </div>
    </div>
  );
}
